import React, { useCallback, useEffect, useState } from "react";
import { colors, gaps, padding } from "theme/defaultStyle";
import StewartTable from "controls/global/stewart-table";
import { Box, IconButton, debounce, styled } from "@mui/material";
import { StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import CustomTooltip from "controls/global/custom-tooltip";
import ToggleOffIcon from "theme/icons/ToggleOffIcon";
import ToggleOnIcon from "theme/icons/ToggleOnIcon";
import MessageDialog from "./MessageDialog";
import FlashMessageV2 from "controls/global/flash-message/FlashMessageV2";
import { useFlashMessageV2Actions } from "utils/context/FlashMessageV2Context";
import { FlashMessageType, PERMISSIONS } from "utils/data/enum";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { TitleSearchGridCriteria } from "entities/UIModel/TitleSearchGridCriteria";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { showToast } from "controls/global/stewart-toast/showToast";
import { useHasSentMessageSuccessfully, useMessage } from "utils/context/TitleMessageContext";
import { Order } from "utils/sorting";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useMyFiles } from "utils/context/MyFilesContext";
import { Message, TitleSearchProduct } from "entities/UIModel";
import { formatDateRegEx } from "utils/shared";

const StyledTableContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  gap: gaps.large1,
  paddingBottom: padding.xlarge,
  "& .MuiTableContainer-root": {
    minHeight: "80px",
    maxHeight: "292px",
    overflowX: "auto",
    paddingLeft: padding.xlarge0,
  },
  "& td:first-of-type": {
    backgroundColor: "unset !important", // "rgba(239, 247, 255, 1) !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar": {
    height: "12px  !important",
    width: "12px  !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar-track": {
    background: "#F9F9F9 !important",
    boxShadow: "1px 0px 0px 0px #DEDEDE inset !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar-thumb": {
    borderRadius: "9px !important",
    backgroundColor: "#00000099 !important",
    border: "3px solid transparent !important",
    backgroundClip: "content-box",
  },

  "& .MuiTableCell-root": {
    paddingBottom: padding.medium1,
    paddingLeft: padding.medium1,
    paddingRight: padding.medium1,
    paddingTop: padding.small1,
    "& > *": {
      lineHeight: "100%",
    },
    "& .MuiButtonBase-root": {
      padding: padding.zero,
    },
    // override row-header-cell for checbox    
    "&.row-header-cell": {
      backgroundColor: "unset",
    }

  },

  "&:hover": {
    backgroundColor: "none",
  },

  "& tr #messageText": {
    maxWidth: "20% !important",
  },

  "& thead": {
    "& tr th#messageText": {
      minWidth: "30% !important",
      maxWidth: "30% !important",
    },
    "& tr th#messageUploadedDate": {
      width: "170px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#messageUploadedByUserName": {
      width: "200px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#messageSource": {
      width: "120px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#Actions": {
      width: "90px !important",
      whiteSpace: "nowrap",
    },
  },
  "& tbody .MuiTableRow-root": {
    "&.resware": {
      backgroundColor: `${colors.lightBlue} !important`,
    },
  },
  "& tbody tr": {
    // backgroundColor: "rgba(239, 247, 255, 1) !important",
    "&:hover": {
      backgroundColor: "white",
    },
  },
  "& tbody tr.toggled .MuiTableCell-root": {
    color: "#7F7F7F",
  },
}));

interface Props {
  page?: number;
  reloadFile?: () => Promise<void>;
  productIndex: number;
  isProductStatusVisible ?: any;
  disabled: boolean;
}

const MessageGrid: React.FC<Props> = ({ reloadFile, productIndex ,isProductStatusVisible, disabled = false, }) => {
  const [data, setData] = useState<Message[]>([]);
  const { getValues } = useFormWrapper();
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const InitialMessage = "Loading results...";
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([InitialMessage]);
  const [{ profileSettings }] = useProfileSettingsCache();
  const [filters, setFilters] = useState<any>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  let orderId: number = getValues(`titleSearchProducts.${productIndex}.orderID`) || 0;

  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [, { showFlashMessage }] = useFlashMessageV2Actions();
  const [, { setRequestTimeoutMs }] = useMyFiles();

  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;
  const NO_RECORD_MESSAGE = "No Messages found";
  const [order, setOrder] = React.useState<Order>("desc");
  const hasHideUnHideAccess = permissionCodes?.includes(PERMISSIONS.TITLESEARCH_HIDEUNHIDEMESSAGES);
  const [, { getMessageByOrder, toggleMessageHidden }] = useMessage();
  const FETCHING_DATA_ERROR_MESSAGE = "There was a problem fetching your messages. Please try again";
  const [orderBy, setOrderBy] = React.useState<keyof Message | undefined>("messageCreatedDate");
  const { generalConfig } = useConfigContext();
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const currentProduct: TitleSearchProduct = getValues(`titleSearchProducts.${productIndex}`);
  const [{ hasSentMessageSuccessfully, isLoading }] = useHasSentMessageSuccessfully();
  
  // const isToggled = selectedMessage?.messageID !== undefined
  // ? !!toggleStates[selectedMessage.messageID]
  // : false;

  const handleRowClick = (messageID: number) => {
    const message = data.find((d) => d.messageID === messageID);
    if (message) {
      setSelectedMessage(message);
      setIsDialogOpen(true);
      setToggleStates((prev) => ({
        ...prev,
        [messageID]: message.isHidden ?? false,
      }));      
    }
  };

  const handleToggle = async (messageID: number, newIsHidden: number) => {
    try {
      await toggleMessageHidden(messageID, newIsHidden,getValues("id"));
      setToggleStates(prev => ({
        ...prev,
        [messageID]: newIsHidden === 1,
      }));
    } catch (err) {
      console.error("Toggle failed", err);
    }
  };
  
  const handleRequestSort = async (columnName: any, disableToggling: boolean = false) => {
    let sortBy = columnName;
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof Message);
    setSortColumn(sortBy);
  };

  const getDefaultFilters = (advanced: boolean): TitleSearchGridCriteria => {
    const sortColumn = "-messageCreatedDate";

    const defaultFilters: TitleSearchGridCriteria = {
      fileId: getValues("id"),
      orderId: getValues(`titleSearchProducts.${productIndex}.orderID`),
      listCount: 0,
      requestedPage: 1,
      isInitialLoad: false,
      sortColumn: sortColumn,
    };
    return defaultFilters;
  };

  const getFileSearchCriteria = () => {
    let criteria: TitleSearchGridCriteria;

    criteria = {
      ...filters,
      listCount: 0,
      requestedPage: 1,
      ...(globalAccessAgencyID && { companyId: globalAccessAgencyID }),
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }
    return criteria;
  };

  const performSearch = async () => {
    let criteria = getFileSearchCriteria();
    let documents: Message[] | undefined = [];
    if ((criteria.orderId || 0) > 0) {
      try {
        documents = await getMessageByOrder({ ...criteria });
        if (!documents) setNoRecordsMessage([NO_RECORD_MESSAGE]);
        if (documents !== undefined) {
          setData(documents);
          if (!documents?.length) {
            setNoRecordsMessage([NO_RECORD_MESSAGE]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast({
          type: "error",
          title: FETCHING_DATA_ERROR_MESSAGE,
        });
      }
    }
  };

  const canSearch = (): boolean => {
    return !partialFilterUpdate && filters !== undefined && Object.values(filters).length > 0;
  };

  useEffect(() => {
    if (readyToSearch) {  //} || isProductStatusVisible ||orderId ) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
  }, [readyToSearch]); // , currentProduct]);

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };
  const debounceSetReadyToSearch = useCallback( 
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs],
  );

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setSettingsLoaded(true);
    };

    init();
  }, []);

    useEffect(() => {
    if (hasSentMessageSuccessfully) {
      if (!isLoading) {
       performSearch()
      }
    }
  }, [hasSentMessageSuccessfully]);

  const columns: StewartTableColumn<any>[] = [
    {
      field: "messageText",
      name: "MESSAGES",
      sortable: true,
      width: 472,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box
            onClick={() => handleRowClick(row.messageID)}
            sx={{
              cursor: "pointer",
              color: isHidden ? colors.grey11 : "inherit",
            }}
          >
            <TooltipCell checkDataLimit={true} title={row.messageText || ""} />
          </Box>
        );
      },
    },
    {
      field: "messageCreatedDate",
      name: "SENT DATE/TIME",
      sortable: true,
      width: 200,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;
        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell title={formatDateRegEx(row.messageCreatedDate || "") || ""} />
          </Box>
        );
      },
    },
    {
      field: "originationUser",
      name: "SENT BY",
      width: 300,
      sortable: true,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.originationUser || ""} />
          </Box>
        );
      },
    },
    {
      field: "originationSource",
      name: "SOURCE",
      sortable: true,
      width: 200,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.originationSource || ""} />
          </Box>
        );
      },
    },
    
    {
      field: "Actions",
      name: "",
      sortable: false,
      valueGetter: (row: Message) => {
        const localToggle = toggleStates[row.messageID];
        const isHidden = typeof localToggle === "boolean" ? localToggle : row.isHidden === 1;

        if (!hasHideUnHideAccess) return null;

        return (
          <CustomTooltip
            title={isHidden ? "Unhide Message" : "Hide Message"}
            placement="top"
            arrow
          >
            <IconButton
              disabled={disabled}
              onClick={() => {
                const newIsHidden = isHidden ? 0 : 1;
                toggleMessageHidden(row.messageID, newIsHidden,getValues("id"));
                setToggleStates((prev) => ({
                  ...prev,
                  [row.messageID]: newIsHidden === 1,
                }));

                if (newIsHidden === 1) {
                  const hideMsg = "Message hidden successfully.";
                  const hideDetail =
                    "Hiding messages will prevent them from appearing to external users, but they can be unhidden by using the unhide button.";

                  setConfirmationMessage(hideMsg);
                  setShowConfirmation(true);
                  showFlashMessage(FlashMessageType.Warning, hideMsg, hideDetail);
                }
              }
            
            }
              className="actionButton"
              sx={{ color: colors.blue01 ,height :"7px" ,width :"45px" }}
            >
              {!isHidden ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </CustomTooltip>
        );
      },
    },
  ];

  useEffect(() => {
    setShowConfirmation(false);
  }, []);

  useEffect(() => {
    const defaultFilters = getDefaultFilters(false);
    setFilters(defaultFilters);
  }, [currentProduct]);

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [filters, sortColumn, settingsLoaded, toggleStates,isProductStatusVisible ,orderId]);

return (
  <>
    {data?.length > 0 && (
      <StyledTableContainer>
        <StewartTable
          cols={columns}
          rows={data.map((row) => ({
            ...row,
            className: toggleStates[row.messageID] ? "toggled" : "",
          }))}
          page={1}
          rowsPerPage={9999}
          showActionRow={false}
          onColumnResize={() => {}}
          orderBy={orderBy}
          order={order}
          onRequestSort={handleRequestSort}
          noRowsMessage={noRecordsMessage}
        />

        {selectedMessage && (
          <MessageDialog
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            message={selectedMessage}
            isToggled={!!toggleStates[selectedMessage.messageID]}
            onToggle={handleToggle}
          />
        )}

        <FlashMessageV2
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={
            showConfirmation ||
            (selectedMessage ? toggleStates[selectedMessage.messageID] : false)
          }
          onClose={() => setShowConfirmation(false)}
          message={confirmationMessage}
        />
      </StyledTableContainer>
    )}
  </>
);

};

export default MessageGrid;
